type SortableWorkspaceRoutine = {
  inProgressOrNextWorkspaceOccasion?: {
    storesCompletedCount: number;
    storesCount: number;
    issueCount: number;
  } | null;
  id: string;
  title: string;
  orgTopic?: {
    name: string;
  } | null;
  currentSchedule: {
    __typename: string;
    endDay?: number | null;
    startDay?: number | null;
    startWeekday?: number | null;
    endWeekday?: number | null;
    weekdays?:
      | readonly {
          weekday: number;
          startTime: { hour: number; minute: number } | null;
        }[]
      | null;
  } | null;
};

export type WorkspaceRoutinesOrderBy =
  | "title"
  | "freq"
  | "issueCount"
  | "status"
  | "topic";

const sortWithFallback =
  (fun: (a: SortableWorkspaceRoutine, b: SortableWorkspaceRoutine) => number) =>
  (a: SortableWorkspaceRoutine, b: SortableWorkspaceRoutine) => {
    const result = fun(a, b);
    if (result !== 0) {
      return result;
    }
    const titleValue = a.title.localeCompare(b.title);
    if (titleValue !== 0) {
      return titleValue;
    }

    return a.id.localeCompare(b.id);
  };

function getFreqSortValue(freq: string | undefined) {
  switch (freq) {
    case "RoutineScheduleDaily":
      return 3;
    case "RoutineScheduleWeekly":
      return 2;
    case "RoutineScheduleMonthly":
      return 1;
    default:
      return 0;
  }
}

function sortSchedule(
  a: SortableWorkspaceRoutine,
  b: SortableWorkspaceRoutine
): number {
  if (a.currentSchedule?.__typename === b.currentSchedule?.__typename) {
    switch (a.currentSchedule?.__typename) {
      case "RoutineScheduleDaily":
        return (
          (b.currentSchedule?.weekdays?.[0]?.startTime?.hour ?? -100) * 60 +
          (b.currentSchedule?.weekdays?.[0]?.startTime?.minute ?? -100) -
          ((a.currentSchedule?.weekdays?.[0]?.startTime?.hour ?? -100) * 60 +
            (a.currentSchedule?.weekdays?.[0]?.startTime?.minute ?? -100))
        );
      case "RoutineScheduleWeekly":
        return (
          (a.currentSchedule.startWeekday ?? -100) * 10 +
          (a.currentSchedule.endWeekday ?? -100) -
          (b.currentSchedule?.startWeekday ?? -100) * 10 +
          (b.currentSchedule?.endWeekday ?? -100)
        );
      case "RoutineScheduleMonthly":
        return (
          (a.currentSchedule.startDay ?? -100) * 10 +
          (a.currentSchedule.endDay ?? -100) -
          (b.currentSchedule?.startDay ?? -100) * 10 +
          (b.currentSchedule?.endDay ?? -100)
        );

      default:
        return 0;
    }
  }
  return (
    getFreqSortValue(a.currentSchedule?.__typename) -
    getFreqSortValue(b.currentSchedule?.__typename)
  );
}

function getSortFunction(orderBy: WorkspaceRoutinesOrderBy) {
  switch (orderBy) {
    case "title":
      return (a: SortableWorkspaceRoutine, b: SortableWorkspaceRoutine) =>
        b.title.localeCompare(a.title);
    case "freq":
      return sortSchedule;
    case "issueCount":
      return (a: SortableWorkspaceRoutine, b: SortableWorkspaceRoutine) =>
        (a.inProgressOrNextWorkspaceOccasion?.issueCount ?? 0) -
        (b.inProgressOrNextWorkspaceOccasion?.issueCount ?? 0);
    case "status":
      return (a: SortableWorkspaceRoutine, b: SortableWorkspaceRoutine) => {
        const aPercentage = a.inProgressOrNextWorkspaceOccasion
          ? (a.inProgressOrNextWorkspaceOccasion.storesCompletedCount /
              a.inProgressOrNextWorkspaceOccasion.storesCount) *
            100
          : 101;
        const bPercentage = b.inProgressOrNextWorkspaceOccasion
          ? (b.inProgressOrNextWorkspaceOccasion.storesCompletedCount /
              b.inProgressOrNextWorkspaceOccasion.storesCount) *
            100
          : 101;
        return bPercentage - aPercentage;
      };
    case "topic":
      return (a: SortableWorkspaceRoutine, b: SortableWorkspaceRoutine) => {
        // If both have topics, compare them
        if (a.orgTopic?.name && b.orgTopic?.name) {
          return a.orgTopic.name.localeCompare(b.orgTopic.name);
        }
        // If only one has a topic, prioritize the one with a topic
        if (a.orgTopic?.name) return -1;
        if (b.orgTopic?.name) return 1;
        // If neither has a topic, fall back to title comparison
        return a.title.localeCompare(b.title);
      };
  }
}
export function workspaceRoutinesSort<
  T extends SortableWorkspaceRoutine,
>(props: {
  orderBy: WorkspaceRoutinesOrderBy;
  order: "ASC" | "DESC";
  routines: T[];
}) {
  const sortFunction = sortWithFallback(getSortFunction(props.orderBy));
  if (props.order === "DESC") {
    return props.routines.sort((a, b) => -sortFunction(a, b));
  }
  return props.routines.sort(sortFunction);
}
